// src/utils/validation.js

// Verhoeff algorithm tables
const dTable = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
];

const pTable = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
];

/**
 * Validates a 12-digit Aadhaar number using the Verhoeff checksum algorithm.
 * @param {string} aadhaar - The Aadhaar number string
 * @returns {boolean} True if valid, false otherwise
 */
export function validateAadhaar(aadhaar) {
  if (!aadhaar) return false;
  const clean = aadhaar.replace(/[\s-]/g, ""); // Strip spaces or hyphens
  if (!/^\d{12}$/.test(clean)) return false;

  let c = 0;
  const digits = clean.split("").reverse().map(Number);

  for (let i = 0; i < digits.length; i++) {
    c = dTable[c][pTable[i % 8][digits[i]]];
  }

  return c === 0;
}

/**
 * Validates a Permanent Account Number (PAN) according to the format AAAAA9999A.
 * @param {string} pan - The PAN string
 * @returns {boolean} True if valid, false otherwise
 */
export function validatePAN(pan) {
  if (!pan) return false;
  const clean = pan.trim().toUpperCase();
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(clean);
}

/**
 * Validates an Indian Financial System Code (IFSC).
 * Format: 4 alphabetic characters, followed by a '0', followed by 6 alphanumeric characters.
 * @param {string} ifsc - The IFSC string
 * @returns {boolean} True if valid, false otherwise
 */
export function validateIFSC(ifsc) {
  if (!ifsc) return false;
  const clean = ifsc.trim().toUpperCase();
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(clean);
}

/**
 * Validates a mobile number containing 10 to 15 digits, optionally prefixed by a '+' and country code.
 * @param {string} mobile - The mobile number string
 * @returns {boolean} True if valid, false otherwise
 */
export function validateMobile(mobile) {
  if (!mobile) return false;
  const clean = mobile.trim().replace(/[\s-]/g, "");
  return /^\+?\d{10,15}$/.test(clean);
}

/**
 * Validates emergency contact details relative to employee contact details.
 * @param {string} emergencyMobile - Emergency contact mobile number
 * @param {string} primaryMobile - Primary employee mobile number
 * @param {string} alternateMobile - Alternate employee mobile number (optional)
 * @returns {boolean} True if different, false if matching any employee numbers
 */
export function validateEmergencyContact(emergencyMobile, primaryMobile, alternateMobile = "") {
  if (!emergencyMobile || !primaryMobile) return false;
  
  const clean = (num) => num.trim().replace(/[^\d]/g, "").slice(-10);
  const cleanEmergency = clean(emergencyMobile);
  const cleanPrimary = clean(primaryMobile);
  const cleanAlternate = alternateMobile ? clean(alternateMobile) : "";

  if (cleanEmergency.length < 10 || cleanPrimary.length < 10) return false;
  if (cleanEmergency === cleanPrimary) return false;
  if (cleanAlternate && cleanEmergency === cleanAlternate) return false;
  return true;
}
